// Phase 68 — BRANDS is auto-derived from BRAND_MODELS keys so adding a new
// brand to brandModels.js automatically propagates here. Sorted alphabetically.
import { BRAND_MODELS } from "./brandModels";
export const BRANDS = Object.keys(BRAND_MODELS).sort((a, b) => a.localeCompare(b, "de"));

export const FUELS = ["benzin", "diesel", "elektro", "hybrid"];
export const TRANSMISSIONS = ["manuell", "automat"];
export const BODY_TYPES = ["limousine", "kombi", "suv", "cabriolet", "coupé", "kleinwagen", "van"];
export const CONDITIONS = ["neu", "occasion", "vorfuhr", "oldtimer"];
export const SELLER_TYPES = ["private", "dealer"];
export const EURO_NORMS = ["Euro 4", "Euro 5", "Euro 6", "Euro 6d"];
export const DRIVETRAINS = ["vorderradantrieb", "hinterradantrieb", "allrad"];
export const DOOR_OPTIONS = [2, 3, 4, 5];
export const SEAT_OPTIONS = [2, 4, 5, 6, 7, 8, 9];
export const CYLINDER_OPTIONS = [2, 3, 4, 5, 6, 8, 10, 12];
// Common HP presets for power range filter
export const POWER_PRESETS_HP = [60, 90, 120, 150, 200, 250, 300, 400, 500];

// Premium color palette for filter circles. value = stored DB value (lowercase),
// hex = rendered swatch, label keys map to translation table.
export const EXTERIOR_COLORS = [
  { value: "schwarz",  hex: "#0b0b0d", label: "Schwarz" },
  { value: "weiss",    hex: "#f4f4f5", label: "Weiss" },
  { value: "silber",   hex: "#bfc3c8", label: "Silber" },
  { value: "grau",     hex: "#6f7480", label: "Grau" },
  { value: "rot",      hex: "#dc2626", label: "Rot" },
  { value: "blau",     hex: "#1d4ed8", label: "Blau" },
  { value: "grün",     hex: "#15803d", label: "Grün" },
  { value: "gelb",     hex: "#facc15", label: "Gelb" },
  { value: "orange",   hex: "#f97316", label: "Orange" },
  { value: "braun",    hex: "#7c4a25", label: "Braun" },
  { value: "beige",    hex: "#cdb89a", label: "Beige" },
  { value: "gold",     hex: "#c89e3c", label: "Gold" },
];

export const INTERIOR_COLORS = [
  { value: "schwarz", hex: "#0b0b0d", label: "Schwarz" },
  { value: "grau",    hex: "#6f7480", label: "Grau" },
  { value: "beige",   hex: "#cdb89a", label: "Beige" },
  { value: "braun",   hex: "#7c4a25", label: "Braun" },
  { value: "weiss",   hex: "#f4f4f5", label: "Weiss" },
  { value: "rot",     hex: "#dc2626", label: "Rot" },
];

// Equipment / Ausstattung – grouped catalog
export const FEATURE_CATEGORIES = [
  {
    id: "comfort",
    label: { de: "Komfort", en: "Comfort" },
    items: [
      "Klimaanlage", "Klimaautomatik", "Sitzheizung", "Lenkradheizung",
      "Tempomat", "Adaptiver Tempomat", "Schiebedach", "Panoramadach",
      "Standheizung", "Memory-Sitze", "Keyless Go", "Elektrische Heckklappe",
    ],
  },
  {
    id: "multimedia",
    label: { de: "Multimedia & Navi", en: "Multimedia & Navi" },
    items: [
      "Navigationssystem", "Bluetooth", "Apple CarPlay", "Android Auto",
      "DAB+ Radio", "Premium Soundsystem", "USB-Anschluss", "Bordcomputer",
      "Head-up Display", "Touchscreen", "Sprachsteuerung",
    ],
  },
  {
    id: "safety",
    label: { de: "Sicherheit", en: "Safety" },
    items: [
      "ABS", "ESP", "Airbags", "Spurhalteassistent", "Notbremsassistent",
      "Toter-Winkel-Assistent", "Parksensoren vorne", "Parksensoren hinten",
      "Rückfahrkamera", "360°-Kamera", "ISOFIX", "Müdigkeitswarner",
      "Verkehrszeichenerkennung",
    ],
  },
  {
    id: "interior",
    label: { de: "Innenraum", en: "Interior" },
    items: [
      "Lederausstattung", "Alcantara", "Sportsitze", "Elektrische Sitzverstellung",
      "Mittelarmlehne", "Ambientebeleuchtung", "Klappbare Rücksitze", "Dachhimmel schwarz",
    ],
  },
  {
    id: "exterior",
    label: { de: "Aussen", en: "Exterior" },
    items: [
      "LED-Scheinwerfer", "Xenon", "Nebelscheinwerfer", "Anhängerkupplung",
      "Alufelgen", "Dachreling", "Getönte Scheiben", "Sportauspuff",
      "Heckspoiler", "Schiebetür",
    ],
  },
  {
    id: "extras",
    label: { de: "Extras", en: "Extras" },
    items: [
      "Sport-Paket", "M-Paket", "AMG-Paket", "S-Line", "R-Line",
      "Winterreifen inklusive", "2. Reifensatz", "Garantie-Verlängerung",
      "Service-Heft", "Nichtraucher", "Erste Hand", "Scheckheftgepflegt",
      "Standheizung", "Dashcam", "Tier-frei",
    ],
  },
];

export const ALL_FEATURES = FEATURE_CATEGORIES.flatMap((c) => c.items);

export const fmtCHF = (n) => new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(n || 0);
// Phase 56 — Product pricing helper that preserves cents (9.90 → "CHF 9.90"),
// while still hiding trailing .00 for whole-franc amounts (49 → "CHF 49").
export const fmtCHFprice = (n) => {
  const num = Number(n || 0);
  const hasCents = Math.abs(num * 100 - Math.round(num) * 100) > 0.5;
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(num);
};
export const fmtKM = (n) => new Intl.NumberFormat("de-CH").format(n || 0);
